/** @file EventEditor: edits one clip event (type, node, mode); the row saves it with clip.setEvents. */
import type { ClipEvent } from '@zilo/pose3d-format/types/clip';
import type { ReactElement } from 'react';
import { useState } from 'react';

const TYPES: ClipEvent['type'][] = ['step', 'hook1', 'hook2', 'hook3', 'hit', 'sound', 'custom'];
const MODES = ['fixed', 'follow', 'stop'] as const;

/** The event, the node names to pick from, and what Save / Delete / Cancel do. */
export type EventEditorProps = {
  event: ClipEvent; nodes: string[];
  onSave: (event: ClipEvent) => void; onDelete: () => void; onCancel: () => void;
};

/** A small inline form; `node` and `mode` may be cleared (absent = no node / the type's default mode). */
export function EventEditor(props: EventEditorProps): ReactElement {
  const [draft, setDraft] = useState<ClipEvent>(props.event);
  const patch = (p: Partial<ClipEvent>): void => {
    const next: ClipEvent = { ...draft, ...p };
    if (next.node === undefined) delete next.node;
    if (next.mode === undefined) delete next.mode;
    setDraft(next);
  };
  return (
    <div role="group" aria-label="event editor" style={{ display: 'flex', gap: 6, alignItems: 'center', padding: 4, fontSize: 11 }}>
      <select aria-label="event type" value={draft.type} onChange={(e) => { patch({ type: e.currentTarget.value as ClipEvent['type'] }); }}>
        {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <select aria-label="event node" value={draft.node ?? ''} onChange={(e) => { patch({ node: e.currentTarget.value === '' ? undefined : e.currentTarget.value }); }}>
        <option value="">(no node)</option>
        {props.nodes.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <select aria-label="event mode" value={draft.mode ?? ''}
        onChange={(e) => { const m = MODES.find((x) => x === e.currentTarget.value); patch({ mode: m }); }}>
        <option value="">(default)</option>
        {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <button type="button" onClick={() => { props.onSave(draft); }}>Save</button>
      <button type="button" onClick={props.onDelete}>Delete</button>
      <button type="button" onClick={props.onCancel}>Cancel</button>
    </div>
  );
}
